import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {
  MatPaginator,
  MatPaginatorIntl,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import { finalize, identity, lastValueFrom } from 'rxjs';

import { AvisoView } from '../../../models/AvisoView';
import { AvisoService } from '../../../services/aviso/aviso.service';
import { AvisosViewDetailsComponent } from '../avisos-view-details/avisos-view-details.component';
import { NotificationService } from '../../../services/notification/notification.service';
import { getPaginatorIntl } from '../../../shared/mat-paginator-intl';

@Component({
  selector: 'app-avisos-list',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    FormsModule,
    MatTableModule,
    MatPaginatorModule,
    MatSelectModule,
    MatButtonModule,
    MatSortModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './avisos-list.component.html',
  styleUrl: './avisos-list.component.scss',
  providers: [{ provide: MatPaginatorIntl, useFactory: getPaginatorIntl }],
})
export class AvisosListComponent implements OnInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  allAvisos: AvisoView[] = [];
  avisosList: AvisoView[] = [];
  searchTerm: string = '';

  avisos = new MatTableDataSource<any>();
  colunasTabela = [
    { id: 'titulo', header: 'Título do aviso', propriedade: 'titulo' },
    {
      id: 'nomeRemetente',
      header: 'Remetente',
      propriedade: 'nomeRemetente',
    },
    {
      id: 'dataAgendamento',
      header: 'Recebido em',
      propriedade: 'dataAgendamento',
    },
  ];

  fieldText = '';
  pageSize = 10;
  pageSizeOptions = [5, 10, 15];
  total = 0;
  inicio = 1;
  fim = 10;

  public isLoading = false;

  get displayedColumns(): string[] {
    return this.colunasTabela.map((coluna) => coluna.id);
  }

  constructor(
    private readonly router: Router,
    private avisoService: AvisoService,
    private dialog: MatDialog,
    private notificationService: NotificationService,
  ) {}

  ngOnInit(): void {
    this.avisos.filterPredicate = (data: any, filtro: string) => {
      const titulo = data.titulo?.toLowerCase() || '';
      return titulo.includes(filtro);
    };

    this.isLoading = true;
    this.loadAvisos();
  }

  ngAfterViewInit(): void {
    this.avisos.paginator = this.paginator;
    this.avisos.sort = this.sort;

    // Configura a ordenação padrão (dataAgendamento DESC)
    this.sort.sort({
      id: 'dataAgendamento',
      start: 'desc',
      disableClear: true,
    });

    // Configura a função de ordenação personalizada para datas
    this.avisos.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'dataAgendamento':
          return this.parseDateString(item.dataAgendamento);
        case 'titulo':
          return item.titulo?.toLowerCase() || '';
        default:
          return item[property];
      }
    };

    this.paginator.page.subscribe((event) => this.onPageChange(event));
  }

  onPageChange(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.atualizarContagem(event.pageIndex, event.pageSize);
  }

  atualizarContagem(pageIndex: number, pageSize: number) {
    if (this.total === 0) {
      this.inicio = 0;
      this.fim = 0;
      return;
    }

    this.inicio = pageIndex * pageSize + 1;
    this.fim = Math.min((pageIndex + 1) * pageSize, this.total);
  }

  updateFieldText(event: Event) {
    const query = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.avisos.filter = query;

    this.total = this.avisos.filteredData.length;
    this.atualizarContagem(0, this.pageSize);
  }

  async loadAvisos() {
    try {
      const observableAvisos = this.avisoService.getMeusAvisos().pipe(
        finalize(() => {
          this.isLoading = false;
        }),
      );

      const avisosArray = await lastValueFrom(observableAvisos);

      this.avisos.data = avisosArray.avisos.map((aviso) => {
        const statusFormatado = aviso.status
          ? aviso.status.charAt(0).toUpperCase() +
            aviso.status.slice(1).toLowerCase()
          : '-';

        return {
          ...aviso,
          status: statusFormatado,
          dataAgendamento: this.formatarData(aviso.dataAgendamento) || '-',
        };
      });

      this.total = this.avisos.data.length;
      this.atualizarContagem(0, this.pageSize);
      this.filterAvisos();
    } catch (err) {
      console.error('Falha ao buscar os avisos:', err);
    }
  }

  filterAvisos(filter: string = '') {
    this.avisosList = this.allAvisos.filter((aviso) =>
      aviso.titulo.toLowerCase().includes(filter),
    );
  }

  formatarValor(aviso: any, coluna: any): string {
    if (coluna.format) {
      return coluna.format(aviso[coluna.propriedade]);
    }
    return aviso[coluna.propriedade] || '';
  }

  private formatarData(dataInput: Date | string): string {
    if (!dataInput) {
      return '';
    }
    const data = new Date(dataInput);
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    return `${dia}/${mes}/${ano}`;
  }

  private parseDateString(dateString: string): Date {
    if (!dateString || dateString === '-') return new Date(0);

    const parts = dateString.split('/');
    if (parts.length === 3) {
      return new Date(+parts[2], +parts[1] - 1, +parts[0]);
    }
    return new Date(dateString);
  }

  getStatusClass(status: string): string {
    const statusNormalizado = status?.toLowerCase().replace(/\s/g, '_');
    switch (statusNormalizado) {
      case 'visualizado':
        return 'status-visualizado';
      case 'nao_visualizado':
        return 'status-nao-visualizado';
      case 'erro':
        return 'status-erro';
      default:
        return 'status-default';
    }
  }

  onSortChange(sortState: Sort) {}

  openDetailsModal(avisoClicado: any) {
    const jaEstavaVisualizado =
      avisoClicado.status?.toLowerCase() === 'visualizado';

    // Obter os avisos VISÍVEIS na página atual (já filtrados e ordenados)
    // Usando sortData para garantir a mesma ordenação
    let avisosVisiveis = this.avisos.filteredData || this.avisos.data;

    // Aplicar a ordenação atual aos dados filtrados
    if (this.sort.active && this.sort.direction) {
      avisosVisiveis = this.sortData(avisosVisiveis, this.sort);
    }

    const dialogRef = this.dialog.open(AvisosViewDetailsComponent, {
      width: '600px',
      data: {
        avisoClicado: avisoClicado,
        avisos: avisosVisiveis,
      },
    });

    dialogRef.afterClosed().subscribe(() => {
      this.loadAvisos();
    });

    if (!jaEstavaVisualizado) {
      avisoClicado.status = 'visualizado';
      this.avisos.data = [...this.avisos.data];

      this.avisoService.visualizarAviso(avisoClicado.id).subscribe({
        next: () => {
          this.loadAvisos();
          this.notificationService.handleAvisoVisualized();
        },
        error: (err) => {
          console.error('Erro ao marcar aviso como visualizado', err);
        },
      });
    }
  }

  private sortData(data: any[], sort: Sort): any[] {
    if (!sort.active || sort.direction === '') {
      return data;
    }

    return data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';

      // Usa a mesma função de acesso aos dados do sortingDataAccessor
      const valueA = this.avisos.sortingDataAccessor(a, sort.active);
      const valueB = this.avisos.sortingDataAccessor(b, sort.active);

      return (valueA < valueB ? -1 : 1) * (isAsc ? 1 : -1);
    });
  }
}
